import { Fragment, useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import { useDirectory } from "hooks"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"

import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { EmptyData } from "components/EmptyData"
import { VenueSelector } from "components/VenueSelector"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { Head } from "components/Head"
import { StoreIcon } from "components/icons"
import { MotionDiv, showContent } from "components/motions"
import {
  DirectoryOccupantCard,
  DirectorySkeletonCard,
} from "components/domains/directory"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"

export const DirectoryKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { t } = useTranslation()
  const { openSearch } = useAppUI()
  const {
    handleChangeParentCategory,
    handleChangeSubCategory,
    changeVenueFilter,
    filteredOccupantsSorted,
    sortedParentCategoryWithOccupant,
    subCategoryWithActiveParentCategory,
    parentCategoryId,
    subCategoryId,
    venueFilter,
    ref,
    endScrollRef,
    hasNextPage,
    showSubCategoryFilter,
    venues,
    isMultiVenueProject,
    locationVenueId,
    hasData,
    hasParentCategory,
  } = useDirectory()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.directory) || <StoreIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`DirectoryPage`]?.styleOverrides || {}
  const {
    root,
    filterSection,
    scrollPane,
    gridContainer,
    gridItem,
    categoryFilterBar,
    subCategoryFilterBar,
    emptyDataWrapper,
    venueSelector,
    navigationWrapper,
    actionButton,
    actionContainer,
    toolbarContainer,
    searchBarContainer,
    languageSelectMenu,
  } = styleOverrides

  return (
    <>
      <Head title={t("Directory")} />
      <Layout>
        <Box ref={ref} sx={root}>
          {hasData ? (
            <MotionDiv
              sx={gridContainer}
              variants={showContent}
              initial="initial"
              animate="animate"
            >
              {filteredOccupantsSorted.pages.map((page, pageNum) => {
                return (
                  <Fragment key={pageNum}>
                    {page.data.map((occupant, index) => (
                      <Box
                        sx={gridItem}
                        key={`${
                          occupant?.properties.isAd ? "ad-" : "directory-item"
                        }${occupant.id}`}
                      >
                        {/* TODO:: Render collapse item when hasMultipleLocations*/}
                        <DirectoryOccupantCard
                          data={occupant}
                          isNewShop={occupant?.properties?.isNewShop}
                          isComingSoon={occupant?.properties?.isComingSoon}
                        />
                      </Box>
                    ))}
                  </Fragment>
                )
              })}
              {hasNextPage &&
                Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <DirectorySkeletonCard
                    ref={index === SKELETON_INDEX_TO_REF ? endScrollRef : null}
                    key={index}
                    sx={gridItem}
                  />
                ))}
            </MotionDiv>
          ) : (
            <MotionDiv
              sx={emptyDataWrapper}
              variants={showContent}
              initial="initial"
              animate="animate"
            >
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </MotionDiv>
          )}
          {hasParentCategory && (
            <Box sx={filterSection}>
              <HorizontalScrollPane
                className="category-filterbar"
                stackProps={{
                  sx: scrollPane,
                }}
              >
                <CategoryFilterBar
                  sx={categoryFilterBar}
                  activeCategory={parentCategoryId}
                  categories={sortedParentCategoryWithOccupant}
                  onChange={handleChangeParentCategory}
                />
              </HorizontalScrollPane>
              {showSubCategoryFilter && (
                <HorizontalScrollPane
                  className="subCategory-filterbar"
                  stackProps={{
                    sx: scrollPane,
                  }}
                >
                  <CategoryFilterBar
                    sx={subCategoryFilterBar}
                    activeCategory={subCategoryId}
                    categories={subCategoryWithActiveParentCategory}
                    onChange={handleChangeSubCategory}
                    renderFilterButton={CategoryFilterChip}
                  />
                </HorizontalScrollPane>
              )}
            </Box>
          )}
          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
          <Box key="app-navigation" sx={navigationWrapper}>
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageSelectMenu sx={languageSelectMenu} />
                <LanguageButton variant="contained" sx={actionButton} />
                {/* //TODO:: Add a wheel chair's mode handler function later */}
                {/*  <Button
            variant="contained"
            sx={actionButton}
            onClick={() => {}}
            startIcon={<RestroomWheelchairIcon />}
          /> */}
                {additionalMenu}
                {/* //TODO:: Create Toolbar component and refactor Code */}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
